<div class="border-t border-gray-100">
  <div class="mb-6">
    <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-3">Locais próximos</p>
    <h3 class="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Locais próximos</h3>
    <p class="text-gray-600 leading-relaxed max-w-3xl">
      Veja pontos de interesse encontrados no entorno do imóvel, com base em distância
      aproximada e categorias relevantes para a análise da região.
    </p>
  </div>

  <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6 mb-6">
    <div class="mb-4">
      <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Camadas no mapa</p>
      <h4 class="text-gray-900 text-lg font-bold mb-2">Categorias para leitura do entorno</h4>
      <p class="text-sm text-gray-600 leading-relaxed">
        Use as categorias abaixo como apoio visual para entender serviços, transporte,
        lazer e comércio próximos ao imóvel com dados retornados pela Google Maps Platform.
      </p>
    </div>

    <div class="flex flex-wrap gap-2">
      <button type="button" class="inline-flex items-center rounded-lg border px-3 py-2 text-xs font-semibold transition"
              [class.border-blue-600]="areAllNearbyCategoriesSelected()" [class.bg-blue-600]="areAllNearbyCategoriesSelected()"
              [class.text-white]="areAllNearbyCategoriesSelected()" [class.border-gray-200]="!areAllNearbyCategoriesSelected()"
              [class.bg-white]="!areAllNearbyCategoriesSelected()" [class.text-gray-500]="!areAllNearbyCategoriesSelected()"
              [class.hover:border-gray-300]="!areAllNearbyCategoriesSelected()" [class.hover:bg-gray-50]="!areAllNearbyCategoriesSelected()"
              (click)="selectAllNearbyCategories()">
        Todos
      </button>

      @for (category of nearbyPlaceCategories; track category.id) {
        <button type="button" class="inline-flex items-center rounded-lg border px-3 py-2 text-xs font-medium transition"
                [class.border-blue-600]="isNearbyCategorySelected(category.id)" [class.bg-blue-600]="isNearbyCategorySelected(category.id)"
                [class.text-white]="isNearbyCategorySelected(category.id)" [class.border-gray-200]="!isNearbyCategorySelected(category.id)"
                [class.bg-white]="!isNearbyCategorySelected(category.id)" [class.text-gray-500]="!isNearbyCategorySelected(category.id)"
                [class.hover:border-gray-300]="!isNearbyCategorySelected(category.id)" [class.hover:bg-gray-50]="!isNearbyCategorySelected(category.id)"
                (click)="toggleNearbyCategoryFilter(category.id)">
          {{ category.title }}
        </button>
      }
    </div>
  </div>

  <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
    @if (realEstate.lat && realEstate.lng) {
      <div class="h-80 sm:h-96 lg:h-[520px]">
        <google-map #nearbyPlacesMap [center]="{ lat: realEstate.lat, lng: realEstate.lng }" [zoom]="getNearbyMapZoom()" [options]="{ disableDefaultUI: false, mapTypeControl: false, streetViewControl: false }" width="100%" height="100%" (mapInitialized)="loadNearbyPlaces()">
          <map-marker #propertyMarker="mapMarker" [position]="{ lat: realEstate.lat, lng: realEstate.lng }" [options]="getPropertyMarkerOptions()" (mapClick)="openPropertyCard(propertyMarker)"></map-marker>
          <map-info-window #propertyInfoWindow="mapInfoWindow">
            <div class="w-56">
              @if (getPropertyImage()) {
                <img [src]="getPropertyImage()!" class="w-full h-24 object-cover rounded-md mb-3" alt="Imagem do imóvel">
              }
              <p class="text-gray-900 font-bold text-sm mb-1">{{ realEstate.real_estate_type }}</p>
              <p class="text-gray-500 text-xs mb-2">{{ realEstate.address }}, {{ realEstate.city }} - {{ realEstate.federation_state }}</p>
              @if (getPropertyPrice()) {
                <p class="text-blue-600 font-semibold text-sm">{{ getPropertyPrice() | currency: 'BRL' }}</p>
              }
            </div>
          </map-info-window>

          @for (nearby of getAllFilteredNearbyPlaces(); track nearby.category.id + nearby.place.name) {
            <map-marker #nearbyMarker="mapMarker" [position]="getNearbyPlacePosition(nearby.place)" [options]="getNearbyMarkerOptions(nearby.category, nearby.place)" (mapClick)="openNearbyPlaceCard(nearby.category, nearby.place, nearbyMarker)"></map-marker>
          }

          <map-info-window #nearbyPlaceInfoWindow="mapInfoWindow">
            @if (selectedNearbyPlace) {
              <div class="w-56">
                <p class="text-xs font-semibold text-gray-500 mb-1">{{ selectedNearbyPlace.category.title }}</p>
                <p class="text-gray-900 font-bold text-sm mb-1">{{ selectedNearbyPlace.place.name }}</p>
                <p class="text-gray-500 text-xs">{{ selectedNearbyPlace.place.distanceKm | number:'1.0-1' }} km aprox.</p>
                @if (selectedNearbyPlace.place.neighborhood) {
                  <p class="text-gray-500 text-xs mt-1">{{ selectedNearbyPlace.place.neighborhood }}</p>
                }
              </div>
            }
          </map-info-window>
        </google-map>
      </div>
    } @else {
      <div class="h-80 sm:h-96 lg:h-[520px] bg-gray-50 flex items-center justify-center px-6 text-center">
        <p class="text-gray-500">Mapa indisponível para este imóvel.</p>
      </div>
    }
  </div>

  @if (isLoadingNearbyPlaces) {
    <div class="mb-6 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
      Carregando locais próximos do Google Maps...
    </div>
  }

  @if (nearbyPlacesError) {
    <div class="mb-6 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
      {{ nearbyPlacesError }}
    </div>
  }

  <div class="bg-gray-50 border border-gray-100 rounded-xl p-5 sm:p-6 mb-6">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
      <div>
        <label for="nearby-distance" class="block text-gray-900 font-bold mb-1">Distância máxima</label>
        <p class="text-sm text-gray-500">Filtre os locais exibidos no mapa pela distância aproximada em relação ao imóvel.</p>
      </div>
      <p class="text-gray-900 text-xl font-semibold">{{ nearbyDistanceKm | number:'1.0-1' }} km</p>
    </div>

    <input id="nearby-distance" type="range" class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
           [min]="nearbyDistanceMinKm" [max]="nearbyDistanceMaxKm" step="0.5" [ngModel]="nearbyDistanceKm"
           (ngModelChange)="nearbyDistanceKm = +$event; centerMap()">

    <div class="flex justify-between text-xs text-gray-400 mt-2">
      <span>0,5 km</span>
      <span>10 km</span>
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
    @for (category of getVisibleNearbyCategories(); track category.id) {
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-5 transition hover:-translate-y-0.5 hover:border-blue-100 hover:shadow-md">
        <div class="flex items-start justify-between gap-3 mb-4">
          <div class="flex items-center gap-3 min-w-0">
            <div class="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 text-blue-600 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10.5h.01"/>
              </svg>
            </div>
            <div class="min-w-0">
              <h4 class="text-gray-900 font-bold truncate">{{ category.title }}</h4>
              <p class="text-xs text-gray-500">Locais encontrados em até {{ nearbyDistanceKm | number:'1.0-1' }} km</p>
            </div>
          </div>
          <span class="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-lg text-sm font-semibold"
                [class.bg-blue-50]="getNearbyCategoryCount(category) > 0" [class.text-blue-700]="getNearbyCategoryCount(category) > 0"
                [class.bg-gray-50]="getNearbyCategoryCount(category) === 0" [class.text-gray-400]="getNearbyCategoryCount(category) === 0">
            {{ getNearbyCategoryCount(category) }}
          </span>
        </div>

        <button type="button" class="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 bg-white transition hover:bg-gray-50 hover:border-gray-300" (click)="toggleNearbyCategory(category.id)">
          Ver detalhes
        </button>

        @if (isNearbyCategoryExpanded(category.id)) {
          <div class="mt-4 pt-4 border-t border-gray-100">
            @if (getNearbyCategoryCount(category) > 0) {
              <div class="space-y-3">
                @for (place of getFilteredNearbyPlaces(category); track place.name) {
                  <div class="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <p class="text-sm font-semibold text-gray-900">{{ place.name }}</p>
                    <p class="text-xs text-gray-500 mt-1">{{ place.distanceKm | number:'1.0-1' }} km aprox.</p>
                    @if (place.neighborhood) {
                      <p class="text-xs text-gray-500">{{ place.neighborhood }}</p>
                    }
                    @if (realEstate.lat && realEstate.lng) {
                      <button type="button" class="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700" (click)="focusNearbyPlace(place)">Ver no mapa</button>
                    }
                  </div>
                }
              </div>
            } @else {
              <p class="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">Nenhum local encontrado dentro do raio selecionado.</p>
            }
          </div>
        }
      </div>
    } @empty {
      <div class="md:col-span-2 xl:col-span-4 bg-gray-50 border border-gray-100 rounded-xl p-5 text-sm text-gray-500">
        Selecione uma ou mais categorias em Camadas no mapa para exibir locais próximos.
      </div>
    }
  </div>

  <div class="mt-6 flex items-start gap-3 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-4">
    <svg class="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M11 17h2v-6h-2v6zm1-14C6.48 3 2 7.48 2 13s4.48 10 10 10 10-4.48 10-10S17.52 3 12 3zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-12h2V7h-2v2z"/></svg>
    <p>
      Os locais exibidos são fornecidos pelo Google Maps conforme a disponibilidade da Places API.
      A disponibilidade, distância e funcionamento dos serviços devem ser confirmados
      pelo usuário antes de qualquer decisão.
    </p>
  </div>
</div>
